/**
 * @fileoverview Dynamic Workflow Review Form Component
 * @module components/WorkflowReviewForm
 * 
 * Renders a review/edit form dynamically based on UI schema fetched from S3.
 * This component replaces hardcoded workflow forms (OceanBankForm, AirplaneLeaseForm)
 * with a schema-driven approach.
 * 
 * Supports two modes:
 * 1. Schema-defined sections: Sections and fields defined in ui-schema.json
 * 2. Auto-derived: Fields automatically derived from step 1 JSON output (derive_from_data: true)
 *    - Supports field_overrides for customizing auto-derived field presentation
 *    - Supports hidden_fields to exclude specific fields
 *    - Supports section_overrides for custom grouping
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { PiCaretDown, PiPlus } from 'react-icons/pi';
import { 
  schemaService, 
  WorkflowUiSchema, 
  ReviewFormField,
  ReviewFormSection,
  ReviewFormSubsection,
  FieldOverride,
  SectionOverrideConfig,
} from '../services/schemaService';

/**
 * Converts a date string to HTML date input format (yyyy-MM-dd)
 * Handles various input formats:
 * - MM-dd-yyyy (e.g., "11-20-2025")
 * - MM/dd/yyyy (e.g., "11/20/2025")
 * - Already valid yyyy-MM-dd (returns as-is)
 * - ISO date strings
 */
const toHtmlDateFormat = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  
  // Already in correct format (yyyy-MM-dd)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // MM-dd-yyyy or MM/dd/yyyy format
  const mdyMatch = dateStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  // Try parsing as a date
  const parsed = new Date(dateStr);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }
  
  // Return empty string for invalid dates to avoid console errors
  return '';
};

/**
 * Converts HTML date input format (yyyy-MM-dd) back to display format (MM-dd-yyyy)
 */
const fromHtmlDateFormat = (dateStr: string): string => {
  if (!dateStr) return '';
  
  // If it's in yyyy-MM-dd format, convert to MM-dd-yyyy
  const ymdMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return `${month}-${day}-${year}`;
  }
  
  return dateStr;
};

/**
 * Subcomponent for editing array fields with chips
 */
interface ArrayFieldEditorProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  description?: string;
  readOnly?: boolean;
}

const ArrayFieldEditor: React.FC<ArrayFieldEditorProps> = ({ 
  label, 
  value, 
  onChange, 
  description, 
  readOnly 
}) => {
  const [newItemInput, setNewItemInput] = useState('');
  
  const handleAddItem = () => {
    if (newItemInput.trim()) {
      onChange([...value, newItemInput.trim()]);
      setNewItemInput('');
    }
  };
  
  const handleRemoveItem = (index: number) => {
    const newArray = value.filter((_, i) => i !== index);
    onChange(newArray);
  };
  
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ 
        border: '1px solid', 
        borderColor: 'divider', 
        borderRadius: 1, 
        p: 1.5,
        minHeight: 60,
        backgroundColor: readOnly ? 'action.disabledBackground' : 'background.paper'
      }}>
        {value.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No items
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {value.map((item, index) => (
              <Chip
                key={index}
                label={String(item)}
                size="small"
                onDelete={readOnly ? undefined : () => handleRemoveItem(index)}
                sx={{ maxWidth: '100%' }}
              />
            ))}
          </Box>
        )}
        {!readOnly && (
          <TextField
            size="small"
            placeholder="Add item and press Enter..."
            value={newItemInput}
            onChange={(e) => setNewItemInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddItem();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleAddItem} disabled={!newItemInput.trim()}>
                    <PiPlus size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mt: 1, width: '100%' }}
          />
        )}
      </Box>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

interface WorkflowReviewFormProps {
  /** Practice area (e.g., 'corporate', 'real-estate') */
  practice?: string;
  /** Workflow name (e.g., 'airplane-lease-step-one') */
  workflow?: string;
  /** Pre-loaded UI schema (optional - if not provided, will fetch using practice/workflow) */
  uiSchema?: WorkflowUiSchema | null;
  /** Initial form data (typically from step one output) */
  initialData?: Record<string, any>;
  /** Callback when form data changes */
  onDataChange?: (data: Record<string, any>) => void;
  /** Callback when schema is loaded (provides metadata like continuation info) */
  onSchemaLoaded?: (schema: WorkflowUiSchema) => void;
  /** Whether the form is read-only */
  readOnly?: boolean;
}

/**
 * Convert a snake_case or camelCase key to a human-readable label
 */
function keyToLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Infer field type from value
 */
function inferFieldType(value: any): ReviewFormField['type'] {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (Array.isArray(value)) return 'array'; // Arrays get special handling
  if (typeof value === 'string' && value.length > 100) return 'multiline';
  return 'string';
}

/**
 * Section override format used directly mapping section IDs to their config
 * e.g., { "loan_basics": { "title": "Loan Basics", "collapsed": false, "order": 1 } }
 */
interface DirectSectionOverride {
  title: string;
  description?: string;
  collapsed?: boolean;
  order?: number;
}

/**
 * Normalize section_overrides to handle both formats:
 * 1. Legacy format: { custom_sections: [], field_section_map: {}, section_order: [] }
 * 2. Direct map format: { "loan_basics": { title: "...", collapsed: false, order: 1 }, ... }
 */
function normalizeSectionOverrides(sectionOverrides: any): {
  legacyConfig: SectionOverrideConfig;
  directMap: Record<string, DirectSectionOverride>;
} {
  if (!sectionOverrides) {
    return { legacyConfig: {}, directMap: {} };
  }
  
  // Check if this is the direct map format (has keys that are section IDs with title property)
  const keys = Object.keys(sectionOverrides);
  const isDirectMap = keys.length > 0 && 
    !keys.includes('custom_sections') && 
    !keys.includes('field_section_map') && 
    !keys.includes('section_order') &&
    typeof sectionOverrides[keys[0]] === 'object' &&
    sectionOverrides[keys[0]]?.title;
  
  if (isDirectMap) {
    // Convert direct map to legacy format
    const directMap = sectionOverrides as Record<string, DirectSectionOverride>;
    const sortedSectionIds = Object.entries(directMap)
      .sort(([, a], [, b]) => (a.order ?? 999) - (b.order ?? 999))
      .map(([id]) => id);
    
    return {
      legacyConfig: {
        section_order: sortedSectionIds,
        custom_sections: Object.entries(directMap).map(([id, config]) => ({
          title: config.title,
          collapsed: config.collapsed,
          default_expanded: config.collapsed === false,
          _sectionId: id, // Store original section ID for lookup
        })),
      },
      directMap,
    };
  }
  
  // Already in legacy format
  return { legacyConfig: sectionOverrides as SectionOverrideConfig, directMap: {} };
}

/**
 * Group fields into logical sections based on key prefixes
 * Supports field overrides, hidden fields, and section overrides for hybrid mode
 * 
 * Section overrides can be in two formats:
 * 1. Legacy: { custom_sections: [], field_section_map: {}, section_order: [] }
 * 2. Direct map: { "section_id": { title: "...", collapsed: false, order: 1 }, ... }
 */
export function groupFieldsIntoSections(
  data: Record<string, any>,
  fieldOverrides?: Record<string, FieldOverride>,
  hiddenFields?: string[],
  sectionOverrides?: SectionOverrideConfig | Record<string, DirectSectionOverride>
): ReviewFormSection[] {
  const sections: Map<string, ReviewFormField[]> = new Map();
  const sectionOrder: string[] = [];
  const hiddenSet = new Set(hiddenFields || []);
  
  // Normalize section overrides to handle both formats
  const { legacyConfig, directMap } = normalizeSectionOverrides(sectionOverrides);
  
  // Define common prefixes and their section names (used as fallback)
  const prefixMap: Record<string, string> = {
    'borrower': 'Borrower Information',
    'guarantor': 'Guarantor Information',
    'personal_guarantor': 'Guarantor Information',
    'entity_guarantor': 'Guarantor Information',
    'trust_guarantor': 'Guarantor Information',
    'bad_boy_guarantor': 'Guarantor Information',
    'property': 'Property Information',
    'loan': 'Loan Details',
    'interest': 'Interest & Rates',
    'fixed': 'Interest & Rates',
    'floor': 'Interest & Rates',
    'margin': 'Interest & Rates',
    'index': 'Interest & Rates',
    'term': 'Loan Terms',
    'extension': 'Loan Terms',
    'amortization': 'Loan Terms',
    'prepayment': 'Loan Terms',
    'commitment': 'Loan Terms',
    'reserve': 'Reserves & Escrows',
    'escrow': 'Reserves & Escrows',
    'holdback': 'Reserves & Escrows',
    'stabilization': 'Reserves & Escrows',
    'dscr': 'DSCR & Covenants',
    'ltv': 'Financial Metrics',
    'ltp': 'Financial Metrics',
    'ltc': 'Financial Metrics',
    'cross': 'Cross-Collateralization',
    'bank': 'Bank Information',
    'senior': 'Bank Information',
    'records': 'Recording Information',
  };
  
  // Build section ID to title mapping from direct map
  const sectionIdToTitle: Record<string, string> = {};
  for (const [id, config] of Object.entries(directMap)) {
    sectionIdToTitle[id] = config.title;
  }
  
  // Build field-to-section map including overrides
  const fieldSectionMap = { ...legacyConfig?.field_section_map };
  
  Object.entries(data).forEach(([key, value]) => {
    // Skip hidden fields
    if (hiddenSet.has(key)) return;
    
    // Skip arrays of objects (like guarantors list)
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      return;
    }
    
    // Get field override if exists
    const override = fieldOverrides?.[key];
    
    // Find matching section: 
    // 1. field_override.section (highest priority) - may be section ID or title
    // 2. field_section_map
    // 3. prefix-based auto-grouping
    // 4. "General Information" fallback
    let sectionName = 'General Information';
    
    if (override?.section) {
      // The section from field_override may be a section ID (e.g., "loan_basics")
      // or a section title (e.g., "Loan Basics")
      // First try to resolve it as a section ID, otherwise use it as-is
      sectionName = sectionIdToTitle[override.section] || override.section;
    } else if (fieldSectionMap[key]) {
      // Use field_section_map
      sectionName = fieldSectionMap[key];
    } else {
      // Fall back to prefix-based auto-grouping
      for (const [prefix, name] of Object.entries(prefixMap)) {
        if (key.startsWith(prefix)) {
          sectionName = name;
          break;
        }
      }
    }
    
    // Create field definition with overrides applied
    const inferredType = inferFieldType(value);
    const field: ReviewFormField & { _order?: number } = {
      key,
      label: override?.label || keyToLabel(key),
      type: override?.type || inferredType,
      grid_width: override?.grid_width ?? ((inferredType === 'multiline' || inferredType === 'array') ? 12 : 4),
      description: override?.description,
      options: override?.options,
      multiline_rows: override?.multiline_rows,
      _order: override?.order,
      show_if: override?.show_if,
    };
    
    // Add to section
    if (!sections.has(sectionName)) {
      sections.set(sectionName, []);
      sectionOrder.push(sectionName);
    }
    sections.get(sectionName)!.push(field);
  });
  
  // add fields defined in field_overrides that aren't in the data
  // this ensures the form shows all expected fields even when the AI didn't extract them
  if (fieldOverrides) {
    const processedKeys = new Set<string>();
    for (const fields of sections.values()) {
      for (const f of fields) processedKeys.add(f.key);
    }

    for (const [key, override] of Object.entries(fieldOverrides)) {
      if (processedKeys.has(key) || hiddenSet.has(key) || override.hidden) continue;

      let sectionName = 'General Information';
      if (override.section) {
        sectionName = sectionIdToTitle[override.section] || override.section;
      }

      // infer a default type from the override or fall back to string
      const fieldType = override.type || 'string';
      const field: ReviewFormField & { _order?: number } = {
        key,
        label: override.label || keyToLabel(key),
        type: fieldType,
        grid_width: override.grid_width ?? 4,
        description: override.description,
        options: override.options,
        multiline_rows: override.multiline_rows,
        _order: override.order,
        show_if: override.show_if,
      };

      if (!sections.has(sectionName)) {
        sections.set(sectionName, []);
        sectionOrder.push(sectionName);
      }
      sections.get(sectionName)!.push(field);
    }
  }

  // Sort fields within each section by order (if specified)
  for (const [, fields] of sections) {
    fields.sort((a: any, b: any) => {
      const aOrder = a._order ?? 999;
      const bOrder = b._order ?? 999;
      return aOrder - bOrder;
    });
  }
  
  // Handle custom sections from legacy config
  if (legacyConfig?.custom_sections) {
    for (const customSection of legacyConfig.custom_sections) {
      // Only process sections with include_fields (from legacy format)
      if ((customSection as any).include_fields?.length) {
        const customFields: ReviewFormField[] = [];
        
        for (const fieldKey of (customSection as any).include_fields) {
          // Find and move field from its current section
          for (const [sectionName, fields] of sections.entries()) {
            const fieldIndex = fields.findIndex(f => f.key === fieldKey);
            if (fieldIndex !== -1) {
              customFields.push(fields[fieldIndex]);
              fields.splice(fieldIndex, 1);
              break;
            }
          }
        }
        
        if (customFields.length > 0) {
          sections.set(customSection.title, customFields);
          // Insert at beginning if not in order
          if (!sectionOrder.includes(customSection.title)) {
            sectionOrder.unshift(customSection.title);
          }
        }
      }
    }
  }
  
  // Apply section ordering if specified (works with both formats)
  let orderedSections = sectionOrder;
  if (legacyConfig?.section_order?.length) {
    // section_order may contain section IDs or titles
    // Build a map that handles both
    const orderMap = new Map<string, number>();
    legacyConfig.section_order.forEach((idOrTitle, idx) => {
      // Map both the ID and the resolved title to the same order
      orderMap.set(idOrTitle, idx);
      const title = sectionIdToTitle[idOrTitle];
      if (title) {
        orderMap.set(title, idx);
      }
    });
    
    orderedSections = [...sectionOrder].sort((a, b) => {
      const aIdx = orderMap.get(a) ?? 999;
      const bIdx = orderMap.get(b) ?? 999;
      return aIdx - bIdx;
    });
  }
  
  // Find custom section configs for icons/expansion
  // Build map by title for looking up section config
  const customSectionMap = new Map<string, any>();
  
  // From direct map format
  for (const [, config] of Object.entries(directMap)) {
    customSectionMap.set(config.title, config);
  }
  
  // From legacy format (custom_sections array)
  if (legacyConfig?.custom_sections) {
    for (const s of legacyConfig.custom_sections) {
      customSectionMap.set(s.title, s);
    }
  }
  
  // Convert to section array, filtering out empty sections
  return orderedSections
    .filter(name => (sections.get(name)?.length ?? 0) > 0)
    .map((name, index) => {
      const customConfig = customSectionMap.get(name);
      // Support both default_expanded (true = expanded) and collapsed (true = collapsed)
      const isExpanded = customConfig?.default_expanded ?? 
                         (customConfig?.collapsed !== undefined ? !customConfig.collapsed : index < 3);
      return {
        id: `section_${index}`,
        title: name,
        icon: customConfig?.icon,
        collapsible: true,
        default_expanded: isExpanded,
        fields: sections.get(name) || [],
      };
    });
}

/**
 * Dynamic Workflow Review Form
 * 
 * Fetches UI schema from S3 and renders a form with sections for reviewing
 * and editing workflow output data before continuing to the next step.
 */
export default function WorkflowReviewForm({
  practice,
  workflow,
  uiSchema: providedUiSchema,
  initialData,
  onDataChange,
  onSchemaLoaded,
  readOnly = false,
}: WorkflowReviewFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialData || {});
  const [uiSchema, setUiSchema] = useState<WorkflowUiSchema | null>(providedUiSchema || null);
  const [loading, setLoading] = useState(!providedUiSchema);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  
  // Track if we've initialized expanded sections for derived mode (prevents reset on data change)
  const derivedSectionsInitializedRef = React.useRef(false);
  
  // Store the initial data for section derivation (only captures first non-null value)
  const initialDataForSectionsRef = React.useRef<Record<string, any> | null>(null);
  if (initialData && !initialDataForSectionsRef.current) {
    // Unwrap response wrappers; guard against string final_response from intermediate steps
    const actualData = initialData.responses?.[0]
      || (initialData.final_response && typeof initialData.final_response === 'object' ? initialData.final_response : null)
      || initialData;
    initialDataForSectionsRef.current = actualData;
  }

  // Auto-derive sections from data if derive_from_data is enabled
  // Uses field_overrides, hidden_fields, section_overrides from schema
  // Memoize the initial data keys to avoid recalculating sections when only values change
  const initialDataKeys = useMemo(() => {
    const data = initialDataForSectionsRef.current;
    if (!data) return null;
    return Object.keys(data).sort().join(',');
  }, [initialData]); // Re-check when initialData changes, but only uses stored ref
  
  const derivedSections = useMemo(() => {
    const reviewForm = uiSchema?.review_form as any;
    const actualData = initialDataForSectionsRef.current;
    if (reviewForm?.derive_from_data && actualData) {
      // Use overrides from schema
      const fieldOverrides = reviewForm.field_overrides || {};
      const hiddenFields = reviewForm.hidden_fields || [];
      const sectionOverrides = reviewForm.section_overrides || {};
        
      return groupFieldsIntoSections(
        actualData, 
        fieldOverrides, 
        hiddenFields, 
        sectionOverrides
      );
    }
    return null;
  }, [uiSchema, initialDataKeys]); // Only recalculate when schema or data keys change, not values

  // Fetch UI schema on mount or when practice/workflow changes (skip if uiSchema provided)
  useEffect(() => {
    // If uiSchema was provided via props, use it and skip fetching
    if (providedUiSchema) {
      setUiSchema(providedUiSchema);
      setLoading(false);
      
      // Initialize expanded sections based on default_expanded
      const initialExpanded = new Set<string>();
      if (providedUiSchema.review_form?.sections) {
        providedUiSchema.review_form.sections.forEach(section => {
          if (section.default_expanded !== false) {
            initialExpanded.add(section.id);
          }
        });
      }
      setExpandedSections(initialExpanded);
      
      if (onSchemaLoaded) {
        onSchemaLoaded(providedUiSchema);
      }
      return;
    }
    
    // If no uiSchema provided, fetch it using practice/workflow
    if (!practice || !workflow) {
      setError('Either uiSchema or practice/workflow must be provided');
      setLoading(false);
      return;
    }
    
    const fetchSchema = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await schemaService.getWorkflowUiSchema(practice, workflow);
        
        if (!response) {
          setError(`No UI schema found for ${practice}/${workflow}`);
          return;
        }
        
        setUiSchema(response.uiSchema);
        
        // Initialize expanded sections based on default_expanded
        const initialExpanded = new Set<string>();
        if (response.uiSchema.review_form?.sections) {
          response.uiSchema.review_form.sections.forEach(section => {
            if (section.default_expanded !== false) {
              initialExpanded.add(section.id);
            }
          });
        }
        setExpandedSections(initialExpanded);
        
        // Notify parent about loaded schema
        if (onSchemaLoaded) {
          onSchemaLoaded(response.uiSchema);
        }
      } catch (err: any) {
        console.error('Failed to fetch UI schema:', err);
        setError(err.message || 'Failed to load form schema');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSchema();
  }, [practice, workflow, providedUiSchema, onSchemaLoaded]);

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      // Handle { responses: [...] } or { final_response: {...} } wrapper
      // Guard: only unwrap final_response if it's an object (not a string)
      const actualData = initialData.responses?.[0]
        || (initialData.final_response && typeof initialData.final_response === 'object' ? initialData.final_response : null)
        || initialData;
      setFormData(actualData);
    }
  }, [initialData]);

  // Initialize expanded sections for derived mode (only once, not on every data change)
  useEffect(() => {
    // Only initialize if we have derived sections AND haven't initialized yet
    if (derivedSections && !derivedSectionsInitializedRef.current) {
      const initialExpanded = new Set<string>();
      derivedSections.forEach(section => {
        if (section.default_expanded !== false) {
          initialExpanded.add(section.id);
        }
      });
      setExpandedSections(initialExpanded);
      derivedSectionsInitializedRef.current = true;
    }
  }, [derivedSections]);

  /**
   * Handle field value change
   */
  const handleFieldChange = (fieldKey: string, value: any) => {
    const newData = { ...formData, [fieldKey]: value };
    setFormData(newData);
    onDataChange?.(newData);
  };

  /**
   * Toggle section expansion
   */
  const handleSectionToggle = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  /**
   * Render a single form field based on field definition
   */
  const renderField = (field: ReviewFormField) => {
    // conditional visibility — hide field if show_if condition is not met
    if (field.show_if) {
      const depValue = formData[field.show_if.field];
      const allowed = Array.isArray(field.show_if.value)
        ? field.show_if.value.includes(depValue)
        : depValue === field.show_if.value;
      if (!allowed) return null;
    }

    const value = formData[field.key];
    const gridWidth = field.grid_width || 6;
    
    let fieldComponent: React.ReactNode;
    
    switch (field.type) {
      case 'boolean':
        fieldComponent = (
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                disabled={readOnly}
              />
            }
            label={field.label}
          />
        );
        break;
        
      case 'select':
      case 'dropdown':
        fieldComponent = (
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              label={field.label}
              disabled={readOnly}
            >
              <MenuItem value="">
                <em>Select...</em>
              </MenuItem>
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        break;
        
      case 'number':
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : null)}
            helperText={field.description}
            disabled={readOnly}
          />
        );
        break;

      case 'currency':
        // Currency field with $ prefix and number formatting
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : null)}
            helperText={field.description}
            disabled={readOnly}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        );
        break;

      case 'percentage':
        // Percentage field with % suffix
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value ? Number(e.target.value) : null)}
            helperText={field.description}
            disabled={readOnly}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        );
        break;

      case 'date':
        // Date field - use native date input
        // Convert value to yyyy-MM-dd format for HTML date input
        // Convert back to MM-dd-yyyy when saving
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type="date"
            value={toHtmlDateFormat(value)}
            onChange={(e) => handleFieldChange(field.key, fromHtmlDateFormat(e.target.value))}
            helperText={field.description}
            disabled={readOnly}
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
        break;

      case 'array':
        // Render arrays as chips with ability to add/remove items
        const arrayValue: string[] = Array.isArray(value) ? value : [];
        
        fieldComponent = (
          <ArrayFieldEditor
            label={field.label}
            value={arrayValue}
            onChange={(newArray) => handleFieldChange(field.key, newArray)}
            description={field.description}
            readOnly={readOnly}
          />
        );
        break;
        
      case 'multiline':
      case 'textarea':
        // Handle arrays by converting to JSON string for display
        const displayValue = Array.isArray(value) 
          ? (value.length > 0 ? JSON.stringify(value, null, 2) : '')
          : (value || '');
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            value={displayValue}
            onChange={(e) => {
              // Try to parse as JSON if it looks like an array
              const newValue = e.target.value;
              if (newValue.startsWith('[')) {
                try {
                  handleFieldChange(field.key, JSON.parse(newValue));
                } catch {
                  handleFieldChange(field.key, newValue);
                }
              } else {
                handleFieldChange(field.key, newValue);
              }
            }}
            helperText={field.description}
            disabled={readOnly}
            multiline
            rows={field.multiline_rows || 3}
          />
        );
        break;
        
      case 'string':
      case 'text':
      default:
        fieldComponent = (
          <TextField
            fullWidth
            size="small"
            label={field.label}
            value={value || ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            helperText={field.description}
            disabled={readOnly}
          />
        );
    }
    
    return (
      <Grid item xs={12} sm={gridWidth} key={field.key}>
        {fieldComponent}
      </Grid>
    );
  };

  /**
   * Render a subsection with its fields
   */
  const renderSubsection = (subsection: ReviewFormSubsection) => {
    return (
      <Box key={subsection.id} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="primary" sx={{ mb: 1.5, fontWeight: 600 }}>
          {subsection.title}
        </Typography>
        <Grid container spacing={2}>
          {subsection.fields.map(field => renderField(field))}
        </Grid>
      </Box>
    );
  };

  /**
   * Render a section with subsections and fields
   */
  const renderSection = (section: ReviewFormSection) => {
    const isExpanded = expandedSections.has(section.id);
    const isCollapsible = section.collapsible !== false;
    
    const sectionContent = (
      <Box>
        {/* Render subsections */}
        {section.subsections?.map(subsection => renderSubsection(subsection))}
        
        {/* Render section-level fields */}
        {section.fields && section.fields.length > 0 && (
          <Box sx={{ mt: section.subsections?.length ? 2 : 0 }}>
            {section.subsections?.length ? (
              <Divider sx={{ mb: 2 }} />
            ) : null}
            <Grid container spacing={2}>
              {section.fields.map(field => renderField(field))}
            </Grid>
          </Box>
        )}
      </Box>
    );
    
    if (isCollapsible) {
      return (
        <Accordion 
          key={section.id}
          expanded={isExpanded}
          onChange={() => handleSectionToggle(section.id)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<PiCaretDown size={24} />}>
            <Typography variant="subtitle1" fontWeight="medium">
              {section.icon && <span style={{ marginRight: 8 }}>{section.icon}</span>}
              {section.title}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {sectionContent}
          </AccordionDetails>
        </Accordion>
      );
    }
    
    return (
      <Paper key={section.id} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
          {section.icon && <span style={{ marginRight: 8 }}>{section.icon}</span>}
          {section.title}
        </Typography>
        {sectionContent}
      </Paper>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ ml: 2 }}>Loading form schema...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // No schema loaded
  if (!uiSchema) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No form schema available for this workflow.
      </Alert>
    );
  }

  // Check for review_form structure - allow derive_from_data mode
  const reviewForm = uiSchema.review_form as any;
  const sectionsToRender = derivedSections || reviewForm?.sections;
  
  if (!reviewForm || (!sectionsToRender && !reviewForm.derive_from_data)) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No review form configuration found for this workflow.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Form header */}
      {reviewForm.title && (
        <Typography variant="h6" sx={{ mb: 1 }}>
          {reviewForm.title}
        </Typography>
      )}
      {reviewForm.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {reviewForm.description}
        </Typography>
      )}
      
      {/* Render sections (either derived or schema-defined) */}
      {sectionsToRender?.map(section => renderSection(section))}
    </Box>
  );
}